import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import LabNavigation from '@/components/LabNavigation';

interface Section {
  title: string;
  content: React.ReactNode;
}

const sections: Section[] = [
  {
    title: "The first commit",
    content: (
      <>
        <p className="mb-6">
          My first commit at Plum wasn't code. It was setting up a sales pipeline in a no-code tool.
        </p>
        <p className="mb-6">
          That's the thing about joining early. Your job title says one thing. The work says another. I was hired as a software engineer. In practice, I was whatever the next problem needed me to be.
        </p>
        <p>
          The group insurance market in India covered 250 million lives but was stuck in the past. 60-day setup times. Broker markups up to 100%. Employees confused about their own coverage. We were ten people trying to fix that.
        </p>
      </>
    ),
  },
  {
    title: "The system that taught me everything",
    content: (
      <>
        <p className="mb-6">
          Saurabh Arora, our CTO, gave me what seemed like a straightforward problem: build a unified network hospitals system.
        </p>
        <p className="mb-6">
          Plum was a broker. We worked with multiple insurers, and each one maintained their own list of network hospitals. Our job was to create one unified system across all of them. First attempt of its kind in India.
        </p>
        <p className="mb-6">
          I tried everything. Web scrapers. Rohini ID matching. Scraping insurer web pages. Whatever small API integrations were available. I put records on Algolia. Nothing worked well enough.
        </p>
        <p className="mb-6">
          Then Nitish Mehrotra joined. He had cofounded and exited from his dev tools startup, Kloudi. He looked at what I was building and introduced me to ETLs and what he called "system of systems" thinking.
        </p>
        <p>
          That changed how I approached problems. Stop thinking of data as static things you query. Think of it as flows. Pipelines. The system finally worked.
        </p>
      </>
    ),
  },
  {
    title: "Wearing multiple hats",
    content: (
      <>
        <p className="mb-6">
          There was no single project I "owned." I owned whatever needed owning.
        </p>
        <p className="mb-6">
          Early on, that meant hacking on other people's systems. CRM integrations with HubSpot and Salesforce. Zapier automations when robotic process automation was still new. N8N and Zapier weren't common tools back then. We were automating insurer workflows before most people knew that was possible.
        </p>
        <p className="mb-6">
          I worked on Trinity, a system where many of our services were converging. I didn't design it, but I understood every corner of it.
        </p>
        <p className="mb-6">
          Later, I got to design systems from scratch. Health Benefits was mine. We'd only done insurance benefits on the platform before. I built the system that expanded us into dental checkups, skin care, coupons, discounts from partners. That became an entire new business line.
        </p>
        <p>
          Retail Term Life was another one. Selling D2C insurance inside a B2B app. That was a good project.
        </p>
      </>
    ),
  },
  {
    title: "The people",
    content: (
      <>
        <p className="mb-6">
          The systems I built matter less than the people who shaped how I think.
        </p>
        <p className="mb-6">
          Arijit Bhattacharya was a friend and mentor. Among many things he taught me, the most important was taste - in work, in craft, in life. Most people miss that. Arijit was the bringer of finer things.
        </p>
        <p className="mb-6">
          Rushabh Dadbhawala was the principal engineer. We'd have lunch together, and I'd dump all my questions on him. He was patient. He'd bring unapologetically simple answers and sometimes questions that answered what I thought needed complex explanations. He taught me what systems thinking actually meant. The difference between code that works and code that's good.
        </p>
        <p>
          The value of these people wasn't what they told me. It was how they changed what I noticed.
        </p>
      </>
    ),
  },
  {
    title: "Two things I'd tell you",
    content: (
      <>
        <p className="mb-6">
          If you just joined a ten-person startup as a founding engineer, I have two things.
        </p>
        <p className="mb-6 text-[#2d2a26] text-xl">
          Know why you are here.
        </p>
        <p className="mb-6">
          Not the company mission. Your reason. What you want to learn. What you want to become. The chaos will make you forget if you don't know.
        </p>
        <p className="mb-6 text-[#2d2a26] text-xl">
          Your voice matters disproportionately.
        </p>
        <p>
          In a ten-person company, one person is 10% of the culture. One opinion shapes decisions. One push-back changes direction. Use that. It won't last.
        </p>
      </>
    ),
  },
];

const FoundingEngineerStudy = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const { scrollYProgress } = useScroll();
  const progressWidth = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 300);
  }, []);

  return (
    <div className="min-h-screen bg-[#f8f6f2]">
      {/* Subtle texture */}
      <div
        className="fixed inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Progress bar */}
      <motion.div
        className="fixed top-0 left-0 h-0.5 bg-[#8b8075] z-50"
        style={{ width: progressWidth }}
      />

      {/* Back button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/labs')}
        className="fixed top-8 left-8 z-40 flex items-center gap-2 text-[#8b8075] hover:text-[#2d2a26] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back</span>
      </motion.button>

      {/* Hero */}
      <section className="min-h-[70vh] flex flex-col justify-end px-8 pb-16 pt-32">
        <div className="max-w-3xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 1 }}
          >
            <p className="text-[#8b8075] text-sm tracking-widest uppercase mb-6">
              Notes from 4 years at a startup
            </p>

            <h1 className="text-5xl md:text-7xl font-light text-[#2d2a26] mb-8 leading-[1.1]">
              First Commit to
              <br />
              <span className="italic">Million Lives</span>
            </h1>

            <div className="max-w-xl">
              <p className="text-xl text-[#4a4640] leading-relaxed mb-8">
                From employee #15 to systems serving 500,000+ people. Not a success story. Just notes from the inside.
              </p>
              <p className="text-[#8b8075] text-sm">
                PlumHQ, 2020-2024
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sections */}
      <section className="py-12 px-8">
        <div className="max-w-3xl mx-auto">
          {sections.map((section, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="mb-20 last:mb-0"
            >
              <h2 className="text-2xl md:text-3xl text-[#2d2a26] font-light mb-8">
                {section.title}
              </h2>
              <div className="text-[#4a4640] text-lg leading-[1.9]">
                {section.content}
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Divider */}
      <div className="py-12 px-8">
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-4">
          <div className="h-px bg-[#d4cfc7] flex-1"></div>
          <span className="text-[#8b8075] text-sm">*</span>
          <div className="h-px bg-[#d4cfc7] flex-1"></div>
        </div>
      </div>

      {/* Closing */}
      <section className="py-16 px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[#4a4640] text-lg leading-relaxed mb-8">
              Four years later, the platform serves over 500,000 employees across 4,000+ companies. Claims that used to take months now settle in days.
            </p>
            <p className="text-[#4a4640] text-lg leading-relaxed mb-8">
              The company raised funding, won awards, made lists. That's nice. But the number I think about is different: infrastructure we built touches over a million lives.
            </p>
            <p className="text-[#2d2a26] text-xl italic">
              That's what it looks like from the inside.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <section className="py-12 px-8 border-t border-[#e8e3dc]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#8b8075] text-sm">
            For those who are building.
          </p>
        </div>
      </section>

      {/* Lab Navigation */}
      <LabNavigation currentLabId="founding-engineer" variant="light" />
    </div>
  );
};

export default FoundingEngineerStudy;
